import { memo, ReactNode, useCallback } from "react";
import classNames from "classnames";
import { Alert, AlertBrandCircleIcon, AlertErrorCircleIcon, AlertProps, AlertSuccessCircleIcon, AlertWarningCircleIcon } from "../../../ui/Alert";
import { useTimeout } from "../../utils";
import { useNotification } from "../NotificationContext";

import styles from "./style.module.scss";

type AlertNotificationProps = { type?: "info" | "warning" | "success" | "error" } & Omit<AlertProps, "floating">;
type CustomNotificationProps = { type: "custom" } & { render: (messageKey: string) => ReactNode | null };
export type NotificationProps = {
  messageKey?: string,
  autoCloseTimeout?: number
  autoClose?: boolean,
} & (AlertNotificationProps | CustomNotificationProps)

export const Notification = memo<NotificationProps>((
  {
    messageKey,
    autoCloseTimeout,
    autoClose,
    type,
    ...props
  },
) => {
  const { remove } = useNotification();
  const removeMessage = useCallback(() => remove(messageKey), [ remove, messageKey ]);

  useTimeout(removeMessage!, autoCloseTimeout, !autoClose);

  if (type === "custom") {
    return (props as CustomNotificationProps).render(messageKey!);
  } else {
    const alertProps = props as AlertNotificationProps;

    return (
      <Alert
        icon={(() => {
          switch (type) {
            case "info":
              return <AlertBrandCircleIcon />;
            case "success":
              return <AlertSuccessCircleIcon />;
            case "warning":
              return <AlertWarningCircleIcon />;
            case "error":
              return <AlertErrorCircleIcon />;
          }
        })()}
        {...alertProps}
        className={classNames(alertProps.className, styles.root)}
        floating
        closeButtonProps={{
          ...alertProps.closeButtonProps,
          onClick: removeMessage,
        }}
      />
    );
  }
});